/**
 * Polling intervals configuration for React Query
 * Used to replace Supabase realtime subscriptions with HTTP polling
 */

export const POLLING_INTERVALS = {
  /** Boss HP updates - 60 seconds (boss HP changes slowly, no need for real-time) */
  BOSS: 60000,

  /** Notifications - 30 seconds to balance freshness with server load */
  NOTIFICATIONS: 30000,

  /** Active duels/challenges - 30 seconds for multiplayer features */
  DUELS: 30000,
  CHALLENGES: 30000,

  /** Friend requests and social updates - 30 seconds */
  SOCIAL: 30000,

  /** Missions progress - 60 seconds (less urgent) */
  MISSIONS: 60000,

  /** User profile/inventory - 60 seconds */
  PROFILE: 60000,

  /** Dashboard overview - 30 seconds */
  DASHBOARD: 30000,
} as const

export const STALE_TIMES = {
  /** No caching - always refetch */
  IMMEDIATE: 0,

  /** Data that changes very frequently (real-time features) */
  REALTIME: 1000,      // 1s - rare, consider WebSocket
  
  /** Data that changes frequently */
  FAST: 3000,          // 3s - duels, active challenges
  
  /** Data that changes every few seconds */
  VERY_FAST: 5000,      // 5s - counters, small progress steps

  /** Data that should stay fresh for a short burst */
  RELIABLE: 10000,     // 10s - search results, active filtered lists

  /** Data that changes occasionally */
  NORMAL: 30000,       // 30s - dashboard, notifications, social
  
  /** Data that changes moderately */
  MODERATE: 60000,     // 1min - missions, inventory, themes
  
  /** Data that rarely changes */
  SLOW: 300000,        // 5min - profile, settings, catalogs
  
  /** Static or nearly static data */
  STATIC: 600000,      // 10min - configs, reference data
} as const

/**
 * Garbage collection times (how long data stays in memory after being unused)
 */
export const GC_TIMES = {
  /** Default - 5 minutes */
  DEFAULT: 300000,
  
  /** Short - 1 minute */
  SHORT: 60000,
  
  /** Long - 30 minutes (for shell data, user profile, etc) */
  LONG: 1800000,
} as const

/**
 * Feature-specific stale time recommendations
 * Use these constants instead of hardcoded values
 */
export const FEATURE_STALE_TIMES = {
  // Arena (multiplayer - needs fresh data)
  ARENA_BOSS: STALE_TIMES.MODERATE,
  ARENA_DUELS: STALE_TIMES.FAST,
  ARENA_CHALLENGES: STALE_TIMES.FAST,
  ARENA_LEAGUES: STALE_TIMES.NORMAL,
  ARENA_PET: STALE_TIMES.MODERATE,
  
  // Emporium (inventory changes on purchase)
  INVENTORY: STALE_TIMES.MODERATE,
  SHOP_CATALOG: STALE_TIMES.SLOW,
  THEMES: STALE_TIMES.MODERATE,
  EMPORIUM_PROFILE: STALE_TIMES.MODERATE,
  
  // Manage (user data - moderate updates)
  MANAGE_HABITS: STALE_TIMES.NORMAL,
  MANAGE_TASKS: STALE_TIMES.NORMAL,
  MANAGE_GOALS: STALE_TIMES.NORMAL,
  MANAGE_DIARY: STALE_TIMES.NORMAL,
  
  // Missions (progress updates periodically)
  MISSIONS: STALE_TIMES.MODERATE,
  MISSIONS_SYNC: STALE_TIMES.NORMAL,
  
  // Social (friend requests, etc)
  FRIENDS: STALE_TIMES.NORMAL,
  NOTIFICATIONS: STALE_TIMES.NORMAL,
  SOCIAL_SEARCH: STALE_TIMES.FAST,
  
  // Dashboard — HAB-143: 60s stale time to cut idle refetches (mobile battery drain)
  DASHBOARD: STALE_TIMES.MODERATE,
  DASHBOARD_HABITS: STALE_TIMES.MODERATE,
  DASHBOARD_TASKS: STALE_TIMES.MODERATE,
  DASHBOARD_WIDGETS: STALE_TIMES.MODERATE,
  DASHBOARD_SUMMARY: STALE_TIMES.SLOW,
  
  // Profile (changes infrequently)
  PROFILE: STALE_TIMES.SLOW,
  SETTINGS: STALE_TIMES.SLOW,
  ACCOUNT: STALE_TIMES.SLOW,
  
  // Auth & Billing
  AUTH_SESSION: STALE_TIMES.SLOW,
  BILLING_SUBSCRIPTION: STALE_TIMES.SLOW,
  BILLING_PLANS: STALE_TIMES.STATIC,
  
  // Achievements & Progress
  ACHIEVEMENTS: STALE_TIMES.MODERATE,
  ACHIEVEMENTS_PROGRESS: STALE_TIMES.MODERATE,

  // Analytics — HAB-261: aggregated stats change slowly (5 min)
  ANALYTICS: STALE_TIMES.SLOW,
  
  // Contracts & Cycles
  SEASON_RESET: STALE_TIMES.STATIC,
  YEAR_RESET: STALE_TIMES.STATIC,
} as const
